import { Accordion, ThcCbdIcon } from "@au-schein-de/au-ui-library";
import { CBD_RANGE_OPTIONS, THC_RANGE_OPTIONS } from "@dransay/api/models";
import { useTranslation } from "react-i18next";
import { ClickableCheckboxRow } from "../components/clickable-checkbox-row.component";
import { FilterHeader } from "../filter-header.component";
import { useFiltersContext } from "../filters.context";
import type { FacetCount } from "../types";

export function ThcCbdFilter() {
	const { t } = useTranslation("product");
	const { filters, onFilterChange, facets } = useFiltersContext();

	const activeFiltersCount =
		(filters.thc?.length ?? 0) + (filters.cbd?.length ?? 0);

	return (
		<Accordion.Item
			value="thc-cbd"
			variant="filter"
			data-testid="thccbd-filter"
		>
			<Accordion.Trigger variant="filter" data-testid="thccbd-filter-trigger">
				<FilterHeader
					title={t("filters.thcCbdContent")}
					value={activeFiltersCount}
					icon={<ThcCbdIcon size={5} />}
				/>
			</Accordion.Trigger>
			<Accordion.Content className="space-y-6" variant="filter">
				<CannabinoidRangeRows
					cannabinoid="thc"
					title={t("filters.thcLevel")}
					options={THC_RANGE_OPTIONS}
					value={filters.thc ?? []}
					facetCounts={facets?.thc}
					onChange={(value) => onFilterChange("thc", value)}
				/>
				<CannabinoidRangeRows
					cannabinoid="cbd"
					title={t("filters.cbdLevel")}
					options={CBD_RANGE_OPTIONS}
					value={filters.cbd ?? []}
					facetCounts={facets?.cbd}
					onChange={(value) => onFilterChange("cbd", value)}
				/>
			</Accordion.Content>
		</Accordion.Item>
	);
}

type CannabinoidRangeRowsProps<TId extends string> = {
	cannabinoid: "thc" | "cbd";
	title: string;
	options: readonly { id: TId }[];
	value: readonly TId[];
	facetCounts?: FacetCount[];
	onChange: (value: TId[]) => void;
};

function CannabinoidRangeRows<TId extends string>({
	cannabinoid,
	title,
	options,
	value,
	facetCounts,
	onChange,
}: CannabinoidRangeRowsProps<TId>) {
	const { t } = useTranslation("product");

	const getCount = (rangeId: TId) => {
		// Return undefined only if no facet data at all (initial load without Typesense)
		if (!facetCounts?.length) return undefined;
		// Return 0 for missing values (filtered out by current selection)
		return facetCounts.find((f) => f.value === rangeId)?.count ?? 0;
	};

	const toggle = (rangeId: TId, checked: boolean) => {
		onChange(
			checked ? [...value, rangeId] : value.filter((id) => id !== rangeId),
		);
	};

	return (
		<div className="space-y-3">
			<div className="text-caption-emphasis text-gray-900">{title}</div>
			{options.map((option) => {
				const isChecked = value.includes(option.id);
				const count = getCount(option.id);
				const isDisabled = count === 0 && !isChecked;

				return (
					<ClickableCheckboxRow
						key={option.id}
						size="textSm"
						border="primary"
						checked={isChecked}
						disabled={isDisabled}
						onRowClick={() => toggle(option.id, !isChecked)}
						// @ts-ignore: dynamic key generation
						label={t(`filters.${cannabinoid}Ranges.${option.id}`)}
						count={count}
						data-testid={`${cannabinoid}-filter-${option.id}`}
						rowTestId={`${cannabinoid}-filter-row-${option.id}`}
						className="h-5 w-5"
					/>
				);
			})}
		</div>
	);
}
